// src/pages/finance/subscriptions/SubscriptionsPage.tsx
import React, { useState, useEffect } from "react";
import { Typography, Button, Modal, Form, InputNumber, Select, Input, Descriptions } from "antd";
import { PlusOutlined, StopOutlined, EditOutlined } from "@ant-design/icons";
import { format } from "date-fns";
import { getSessionUser } from "../../../utils/auth/sessionUser";

// Layout
import Layout from "../../../components/Layout";
import PageContainer from "../../../components/shared/PageContainer";
import { SectionCard, DataTable, StatusTag, PageToolbar, DetailDrawer } from "../../../components/shared";

// API
import {
	fetchSubscriptionsByCompany,
	getSubscription,
	createSubscription,
	updateSubscription,
	cancelSubscription,
	formatCurrency,
} from "../../../api/finance/FinanceApi";

// Types
import {
	Subscription,
	SubscriptionStatus,
	CreateSubscriptionRequest,
	UpdateSubscriptionRequest,
	CancelSubscriptionRequest,
} from "../../../types/finance/FinanceTypes";

// Hooks
import { useNotifications } from "../../../context/NotificationContext";
import { fetchClinicCompany } from "../../../api/admin/companyApi";

const { Text } = Typography;
const { confirm } = Modal;
const { Option } = Select;

const SubscriptionsPage: React.FC = () => {
	const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
	const [drawerVisible, setDrawerVisible] = useState<boolean>(false);
	const [createModalVisible, setCreateModalVisible] = useState<boolean>(false);
	const [updateModalVisible, setUpdateModalVisible] = useState<boolean>(false);
	const [form] = Form.useForm();
	const [updateForm] = Form.useForm();

	const { showError, showSuccess } = useNotifications();
	const user = getSessionUser();
	const [companyId, setCompanyId] = useState<string>("");

	useEffect(() => {
		loadSubscriptions();
	}, []);

	const loadSubscriptions = async () => {
		setIsLoading(true);
		try {
			const company = await fetchClinicCompany();
			setCompanyId(company.id.toString());
			const data = await fetchSubscriptionsByCompany();
			setSubscriptions(data);
		} catch (error) {
			showError("Failed to load subscriptions");
		} finally {
			setIsLoading(false);
		}
	};

	const handleViewSubscription = async (subscription: Subscription) => {
		try {
			const { subscription: full } = await getSubscription(subscription.id);
			setSelectedSubscription(full);
			setDrawerVisible(true);
		} catch (error) {
			showError("Failed to load subscription details");
		}
	};

	const handleCreateSubscription = async (values: any) => {
		try {
			const request: CreateSubscriptionRequest = {
				userId: values.userId,
				customerId: values.customerId,
				priceId: values.priceId,
				paymentMethodId: values.paymentMethodId,
				trialPeriodDays: values.trialPeriodDays,
				metadata: values.metadata,
			};
			await createSubscription(request);
			showSuccess("Subscription created successfully");
			setCreateModalVisible(false);
			form.resetFields();
			await loadSubscriptions();
		} catch (error) {
			showError("Failed to create subscription");
		}
	};

	const handleUpdateSubscription = async (values: any) => {
		if (!selectedSubscription) return;
		try {
			const request: UpdateSubscriptionRequest = {
				priceId: values.priceId,
				paymentMethodId: values.paymentMethodId,
				cancelAtPeriodEnd: values.cancelAtPeriodEnd,
			};
			await updateSubscription(selectedSubscription.id, request);
			showSuccess("Subscription updated successfully");
			setUpdateModalVisible(false);
			updateForm.resetFields();
			await loadSubscriptions();
			const { subscription: updated } = await getSubscription(selectedSubscription.id);
			setSelectedSubscription(updated);
		} catch (error) {
			showError("Failed to update subscription");
		}
	};

	const handleCancelSubscription = (subscription: Subscription, cancelAtPeriodEnd = true) => {
		confirm({
			title: cancelAtPeriodEnd ? "Schedule Cancellation" : "Cancel Immediately",
			content: cancelAtPeriodEnd
				? "Cancel this subscription at the end of the current period?"
				: "Are you sure you want to cancel this subscription immediately?",
			okText: "Cancel Subscription",
			okType: "danger",
			onOk: async () => {
				try {
					const request: CancelSubscriptionRequest = { cancelAtPeriodEnd };
					await cancelSubscription(subscription.id, request);
					showSuccess(cancelAtPeriodEnd ? "Subscription will be cancelled at period end" : "Subscription cancelled");
					await loadSubscriptions();
					if (selectedSubscription?.id === subscription.id) {
						const { subscription: updated } = await getSubscription(subscription.id);
						setSelectedSubscription(updated);
					}
				} catch (error) {
					showError("Failed to cancel subscription");
				}
			},
		});
	};

	const filteredSubscriptions = subscriptions.filter(
		(sub) =>
			sub.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
			sub.userId.toLowerCase().includes(searchTerm.toLowerCase()) ||
			sub.customerId.toLowerCase().includes(searchTerm.toLowerCase())
	);

	// ── Columns ──────────────────────────────────────────────────
	const columns = [
		{
			title: "ID",
			dataIndex: "id",
			key: "id",
			width: 120,
			render: (id: string) => <Text type="secondary" style={{ fontSize: 12 }}>{id.substring(0, 10)}…</Text>,
		},
		{
			title: "Customer",
			dataIndex: "customerId",
			key: "customerId",
			width: 140,
			render: (customerId: string) => <Text type="secondary" style={{ fontSize: 12 }}>{customerId.substring(0, 12)}…</Text>,
		},
		{
			title: "Amount",
			dataIndex: "amount",
			key: "amount",
			width: 130,
			render: (amount: number | undefined, record: Subscription) => (
				<Text strong style={{ color: "var(--mg-success)" }}>
					{amount ? formatCurrency(amount, record.currency) : "—"}
				</Text>
			),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 110,
			render: (status: SubscriptionStatus) => <StatusTag status={status} />,
		},
		{
			title: "Current Period",
			key: "period",
			width: 200,
			render: (_: any, record: Subscription) =>
				record.currentPeriodStart && record.currentPeriodEnd ? (
					<Text type="secondary" style={{ fontSize: 13 }}>
						{format(new Date(record.currentPeriodStart), "dd/MM/yy")} — {format(new Date(record.currentPeriodEnd), "dd/MM/yy")}
					</Text>
				) : (
					<Text type="secondary">—</Text>
				),
		},
	];

	// ── Drawer footer actions ─────────────────────────────────────
	const drawerFooter = selectedSubscription?.status === SubscriptionStatus.ACTIVE && !selectedSubscription.cancelAtPeriodEnd ? (
		<>
			<Button
				icon={<StopOutlined />}
				onClick={() => handleCancelSubscription(selectedSubscription, true)}
			>
				Cancel at Period End
			</Button>
			<Button
				danger
				type="primary"
				icon={<StopOutlined />}
				onClick={() => handleCancelSubscription(selectedSubscription, false)}
			>
				Cancel Immediately
			</Button>
		</>
	) : undefined;

	return (
		<Layout>
			<PageContainer
				data-testid="subscription-root"
			>
				<PageToolbar
					primaryAction={{ label: "Create Subscription", icon: <PlusOutlined />, onClick: () => setCreateModalVisible(true), testId: "subscription-create-btn" }}
					filters={[
						{
							type: "search",
							placeholder: "Search subscriptions…",
							value: searchTerm,
							onChange: setSearchTerm,
							width: 260,
						},
					]}
					onReset={() => setSearchTerm("")}
				/>

				<DataTable
					columns={columns}
					dataSource={filteredSubscriptions}
					rowKey="id"
					loading={isLoading}
					pagination={false}
					onRowClick={handleViewSubscription}
					emptyTitle="No subscriptions found"
					emptyDescription="Create your first subscription to get started"
					emptyAction={{ label: "Create Subscription", onClick: () => setCreateModalVisible(true) }}
				/>

				{/* Subscription Details Drawer */}
				<DetailDrawer
					open={drawerVisible}
					onClose={() => { setDrawerVisible(false); setSelectedSubscription(null); }}
					width="70vw"
					title={selectedSubscription ? `Subscription — ${selectedSubscription.id.substring(0, 12)}…` : "Subscription Details"}
					subtitle={selectedSubscription ? <StatusTag status={selectedSubscription.status} /> : undefined}
					headerActions={
						selectedSubscription?.status === SubscriptionStatus.ACTIVE && !selectedSubscription.cancelAtPeriodEnd ? (
							<Button
								icon={<EditOutlined />}
								onClick={() => {
									updateForm.setFieldsValue({ priceId: selectedSubscription.priceId });
									setUpdateModalVisible(true);
								}}
							>
								Update
							</Button>
						) : undefined
					}
					footer={drawerFooter}
				>
					{selectedSubscription && (
						<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
							<SectionCard title="Subscription Details">
								<Descriptions bordered size="small" column={2}>
									<Descriptions.Item label="Subscription ID" span={2}>
										<Text type="secondary" style={{ fontSize: 12 }}>{selectedSubscription.id}</Text>
									</Descriptions.Item>
									<Descriptions.Item label="Status" span={2}>
										<StatusTag status={selectedSubscription.status} />
									</Descriptions.Item>
									<Descriptions.Item label="Customer ID" span={2}>
										<Text type="secondary" style={{ fontSize: 12 }}>{selectedSubscription.customerId}</Text>
									</Descriptions.Item>
									<Descriptions.Item label="Price ID" span={2}>
										<Text type="secondary" style={{ fontSize: 12 }}>{selectedSubscription.priceId}</Text>
									</Descriptions.Item>
									{selectedSubscription.amount && (
										<Descriptions.Item label="Amount" span={2}>
											<Text strong style={{ fontSize: 16, color: "var(--mg-success)" }}>
												{formatCurrency(selectedSubscription.amount, selectedSubscription.currency)}
											</Text>
										</Descriptions.Item>
									)}
									{selectedSubscription.currentPeriodStart && (
										<Descriptions.Item label="Period Start">
											{format(new Date(selectedSubscription.currentPeriodStart), "dd MMM yyyy")}
										</Descriptions.Item>
									)}
									{selectedSubscription.currentPeriodEnd && (
										<Descriptions.Item label="Period End">
											{format(new Date(selectedSubscription.currentPeriodEnd), "dd MMM yyyy")}
										</Descriptions.Item>
									)}
									{selectedSubscription.cancelAtPeriodEnd && (
										<Descriptions.Item label="Cancel at Period End" span={2}>
											<StatusTag status="warning" label="Scheduled" />
										</Descriptions.Item>
									)}
									{selectedSubscription.canceledAt && (
										<Descriptions.Item label="Canceled At" span={2}>
											{format(new Date(selectedSubscription.canceledAt), "dd MMM yyyy HH:mm")}
										</Descriptions.Item>
									)}
									<Descriptions.Item label="Created At" span={2}>
										{format(new Date(selectedSubscription.createdAt), "dd MMM yyyy HH:mm")}
									</Descriptions.Item>
								</Descriptions>
							</SectionCard>
						</div>
					)}
				</DetailDrawer>

				{/* Create Subscription Modal */}
				<Modal
					title="Create Subscription"
					open={createModalVisible}
					onCancel={() => { setCreateModalVisible(false); form.resetFields(); }}
					footer={[
						<Button key="cancel" onClick={() => { setCreateModalVisible(false); form.resetFields(); }}>Cancel</Button>,
						<Button key="submit" type="primary" onClick={() => form.submit()}>Create Subscription</Button>,
					]}
					width={600}
					destroyOnClose
				>
					<Form form={form} layout="vertical" onFinish={handleCreateSubscription}>
						<Form.Item label="User ID" name="userId" rules={[{ required: true, message: "Please enter user ID" }]}>
							<Input placeholder="User ID" />
						</Form.Item>
						<Form.Item label="Customer ID" name="customerId" rules={[{ required: true, message: "Please enter customer ID" }]}>
							<Input placeholder="Stripe Customer ID" />
						</Form.Item>
						<Form.Item label="Price ID" name="priceId" rules={[{ required: true, message: "Please enter price ID" }]}>
							<Input placeholder="Stripe Price ID" />
						</Form.Item>
						<Form.Item label="Payment Method ID" name="paymentMethodId">
							<Input placeholder="Stripe Payment Method ID (optional)" />
						</Form.Item>
						<Form.Item label="Trial Period (Days)" name="trialPeriodDays" initialValue={0}>
							<InputNumber min={0} style={{ width: "100%" }} />
						</Form.Item>
					</Form>
				</Modal>
				{/* Update Subscription Modal */}
				<Modal
					title="Update Subscription"
					open={updateModalVisible}
					onCancel={() => { setUpdateModalVisible(false); updateForm.resetFields(); }}
					footer={[
						<Button key="cancel" onClick={() => { setUpdateModalVisible(false); updateForm.resetFields(); }}>Cancel</Button>,
						<Button key="submit" type="primary" onClick={() => updateForm.submit()}>Update Subscription</Button>,
					]}
					width={600}
					destroyOnClose
				>
					<Form form={updateForm} layout="vertical" onFinish={handleUpdateSubscription}>
						<Form.Item label="Price ID" name="priceId">
							<Input placeholder="New Stripe Price ID" />
						</Form.Item>
						<Form.Item label="Payment Method ID" name="paymentMethodId">
							<Input placeholder="New Stripe Payment Method ID" />
						</Form.Item>
						<Form.Item label="Cancel at Period End" name="cancelAtPeriodEnd">
							<Select>
								<Option value={false}>No</Option>
								<Option value={true}>Yes</Option>
							</Select>
						</Form.Item>
					</Form>
				</Modal>
			</PageContainer>
		</Layout>
	);
};

export default SubscriptionsPage;
